import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from '../../store/types';
import { loadConversationAction } from 'store/conversations/conversationSlice';
import { setSideDrawer } from '../../store/sideDrawer/sideDrawerSlice';
import { eSideDrawerContentType } from '../../types/sideDrawer/eSideDrawerContentType';
import { useHistory } from 'react-router';
import QueryString from 'qs';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';
import { setConversationOrder } from '../../store/conversations/conversationOrderSlice';
import { UUID } from '../../types/shared/UUID';
import { eContactType } from '../../types/conversations/eContactType';
import { FlexRow, LoadingImage } from '../../ui-toolkit/components';
import styled from 'styled-components';
import { getOrderByIDAsyncAction } from '../../store/order/orderInformation/orderInformationSlice';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../store/store';

const FullHeightFlexRow = styled(FlexRow)`
  height: 100%;
`;

const convertQueryStringParamsToQuery = (
  queryStringParams: QueryString.ParsedQs
): Partial<{ orderId?: UUID; contactId?: UUID; contactType?: eContactType; autoSave?: boolean }> => {
  const { orderId, contactId, contactType, autoSave } = queryStringParams;
  return {
    orderId: typeof orderId === 'string' ? orderId.toString() : undefined,
    contactId: typeof contactId === 'string' ? contactId.toString() : undefined,
    contactType: typeof contactType === 'string' ? (contactType as eContactType) : undefined,
    autoSave: typeof autoSave === 'string',
  };
};

export const EditConversationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { conversationId } = useParams<{ conversationId: string }>();
  const contentType = useSelector((state: RootStore) => state.sideDrawer.contentType);

  const inputsFromQuery = useMemo(
    () => convertQueryStringParamsToQuery(QueryString.parse(search, { ignoreQueryPrefix: true })),
    [search]
  );
  const { data: conversation } = useSelector((state: RootStore) => state.conversations.conversation);

  useEffect(() => {
    if (conversationId) {
      dispatch(loadConversationAction(conversationId));
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (conversation.id) dispatch(setSideDrawer(eSideDrawerContentType.Conversation));
  }, [dispatch, conversation]);

  useEffect(() => {
    const contact = {
      ...conversation.contact,
      ...(inputsFromQuery.contactType && { contactType: inputsFromQuery.contactType }),
      ...(inputsFromQuery.contactId && { id: inputsFromQuery.contactId }),
    };

    dispatch(
      setConversationOrder({
        genesysId: conversation.genesysId,
        contact: {
          contactType: contact.contactType,
          id: contact.id,
          firstName: '',
          lastName: '',
        },
        ...(inputsFromQuery.orderId && { orderId: inputsFromQuery.orderId }),
      })
    );
  }, [dispatch, conversation, inputsFromQuery]);

  useEffect(() => {
    if (inputsFromQuery.orderId) dispatch(getOrderByIDAsyncAction(inputsFromQuery.orderId));
  }, [inputsFromQuery, dispatch]);

  useEffect(() => {
    if (contentType !== undefined) {
      const orderId = inputsFromQuery.orderId;
      const search = inputsFromQuery.autoSave ? '?autoSave=true' : '';
      const pathname = orderId ? clientUrls.urlFor.orderStatus(orderId) : clientUrls.urlFor.adminOrders();
      history.push({ pathname, search });
    }
  }, [contentType, inputsFromQuery.orderId, inputsFromQuery.autoSave, history]);

  return (
    <FullHeightFlexRow justifyContent="center" verticalAlign="center">
      <LoadingImage />
    </FullHeightFlexRow>
  );
};
